import { EditorSection } from '../EditorSection';
import { ControlGroup } from '../ControlGroup';
import { useCaps, usePlotEditorContext } from '../PlotEditorContext';
import { usePlotlyUpdate } from '../usePlotlyUpdate';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useState, useEffect, useRef } from 'react';
import { usePlotsStore } from '@/store/plots';

/**
 * Axes & Scales Tab
 * Primary: Scale type, range, format, gridlines
 * Advanced: Custom ticks, tick angle
 */
export function AxesScalesTab() {
  const caps = useCaps();
  const { editorUI, selection } = usePlotsStore();
  const [activeSubtab, setActiveSubtab] = useState<'x-axis' | 'y-axis'>('x-axis');
  const lastFocusHintTs = useRef<number>(0);
  
  // Auto-select subtab based on selection or focusHint
  useEffect(() => {
    const hint = editorUI.focusHint;
    
    // Check focusHint first (one-shot)
    if (hint && hint.axesSubtab && hint.ts && hint.ts !== lastFocusHintTs.current) {
      lastFocusHintTs.current = hint.ts;
      const targetSubtab = hint.axesSubtab === 'x' ? 'x-axis' : 'y-axis';
      if (targetSubtab !== activeSubtab) {
        setActiveSubtab(targetSubtab);
      }
      return;
    }
    
    // Fallback to selection-based subtab selection
    if (selection.kind === 'axis') {
      const targetSubtab = (selection.axisId === 'x' || selection.axisId === 'x2') ? 'x-axis' : 'y-axis';
      if (targetSubtab !== activeSubtab) {
        setActiveSubtab(targetSubtab);
      }
    }
  }, [editorUI.focusHint, selection, activeSubtab]);
  
  // Hide entire tab if axes are not supported
  if (!caps.supports.axesTab) {
    return (
      <div className="space-y-4">
        <div className="space-y-1">
          <h3 className="text-xs font-semibold text-foreground">Axes & Scales</h3>
          <p className="text-[11px] text-muted-foreground">
            Axes controls are not available for this chart type.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Section heading */}
      <div className="space-y-1">
        <h3 className="text-xs font-semibold text-foreground">Axes & Scales</h3>
        <p className="text-[11px] text-muted-foreground">
          Control scale, range, formatting, and gridlines for the X and Y axes.
        </p>
      </div>
      <Tabs value={activeSubtab} onValueChange={(v) => setActiveSubtab(v as 'x-axis' | 'y-axis')} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          {caps.axes.hasX && <TabsTrigger value="x-axis">X Axis</TabsTrigger>}
          {caps.axes.hasY && <TabsTrigger value="y-axis">Y Axis</TabsTrigger>}
        </TabsList>

        {caps.axes.hasX && (
          <TabsContent value="x-axis" className="space-y-4 mt-4">
            <div className="space-y-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                Scale & Range
              </p>
              <XAxisPrimaryGroup />
            </div>

            <EditorSection
              id="x-axis-advanced"
              title="Advanced"
              collapsible
              defaultCollapsed
            >
              <XAxisAdvancedGroup />
            </EditorSection>
          </TabsContent>
        )}

        {caps.axes.hasY && (
          <TabsContent value="y-axis" className="space-y-4 mt-4">
            <div className="space-y-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                Scale & Range
              </p>
              <YAxisPrimaryGroup />
            </div>

            <EditorSection
              id="y-axis-advanced"
              title="Advanced"
              collapsible
              defaultCollapsed
            >
              <YAxisAdvancedGroup />
            </EditorSection>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function XAxisPrimaryGroup() {
  const { plotlyJson, dataColumns } = usePlotEditorContext();
  const { updateAxis, updateLayout } = usePlotlyUpdate();
  
  const xaxis = plotlyJson?.layout?.xaxis || {};
  const [xRange, setXRange] = useState({
    min: xaxis.range?.[0] ?? '',
    max: xaxis.range?.[1] ?? '',
  });

  useEffect(() => {
    const range = plotlyJson?.layout?.xaxis?.range;
    if (range) {
      setXRange({
        min: range[0] ?? '',
        max: range[1] ?? '',
      });
    }
  }, [plotlyJson?.layout?.xaxis?.range]);

  const formatOptions = [
    { value: 'default', label: 'Default' },
    { value: '.0f', label: 'Integer' },
    { value: '.1f', label: '1 Decimal' },
    { value: '.2f', label: '2 Decimals' },
    { value: '.0%', label: 'Percentage' },
    { value: '.2e', label: 'Scientific' },
    { value: '$,.0f', label: 'Currency' },
    { value: '%Y-%m-%d', label: 'Date (YYYY-MM-DD)' },
    { value: '%b %Y', label: 'Date (Mon Year)' },
  ];

  return (
    <div className="space-y-3">
      {/* Data Column */}
      {dataColumns && dataColumns.length > 0 && (
        <ControlGroup label="Data column">
          <Select
            defaultValue={plotlyJson?.data?.[0]?.x}
            onValueChange={(value) => updateLayout('xaxis.column', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select column" />
            </SelectTrigger>
            <SelectContent>
              {dataColumns.map((col) => (
                <SelectItem key={col} value={col}>
                  {col}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ControlGroup>
      )}

      {/* Scale Type */}
      <ControlGroup label="Scale type">
        <Select
          value={xaxis.type || 'linear'}
          onValueChange={(value) => updateAxis('x', 'type', value)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="linear">Linear</SelectItem>
            <SelectItem value="log">Logarithmic</SelectItem>
            <SelectItem value="category">Categorical</SelectItem>
          </SelectContent>
        </Select>
      </ControlGroup>

      {/* Number Format */}
      <ControlGroup label="Number format">
        <Select
          value={xaxis.tickformat || 'default'}
          onValueChange={(value) => {
            if (value === 'default') {
              updateAxis('x', 'tickformat', undefined);
            } else {
              updateAxis('x', 'tickformat', value);
            }
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select format" />
          </SelectTrigger>
          <SelectContent>
            {formatOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </ControlGroup>

      {/* Custom Range */}
      <ControlGroup label="Custom range">
        <div className="grid grid-cols-2 gap-2">
          <Input
            type="number"
            placeholder="Min"
            value={xRange.min}
            onChange={(e) => {
              const newRange = { ...xRange, min: e.target.value };
              setXRange(newRange);
              const range = [
                newRange.min ? parseFloat(newRange.min) : null,
                newRange.max ? parseFloat(newRange.max) : null,
              ];
              updateAxis('x', 'range', range);
            }}
          />
          <Input
            type="number"
            placeholder="Max"
            value={xRange.max}
            onChange={(e) => {
              const newRange = { ...xRange, max: e.target.value };
              setXRange(newRange);
              const range = [
                newRange.min ? parseFloat(newRange.min) : null,
                newRange.max ? parseFloat(newRange.max) : null,
              ];
              updateAxis('x', 'range', range);
            }}
          />
        </div>
      </ControlGroup>

      {/* Label Position */}
      <ControlGroup label="Label position">
        <Select
          value={xaxis.side || 'bottom'}
          onValueChange={(value) => updateAxis('x', 'side', value)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="bottom">Bottom</SelectItem>
            <SelectItem value="top">Top</SelectItem>
          </SelectContent>
        </Select>
      </ControlGroup>

      {/* Gridlines */}
      <div className="pt-2 border-t border-border/50">
        <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground mb-2">
          Gridlines
        </p>
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-muted-foreground">Show gridlines</span>
          <Switch
            checked={xaxis.showgrid !== false}
            onCheckedChange={(checked) => updateAxis('x', 'showgrid', checked)}
          />
        </div>
      </div>
    </div>
  );
}

function XAxisAdvancedGroup() {
  const { plotlyJson } = usePlotEditorContext();
  const { updateAxis } = usePlotlyUpdate();
  const xaxis = plotlyJson?.layout?.xaxis || {};
  const [customTicks, setCustomTicks] = useState(
    Array.isArray(xaxis.tickvals) ? xaxis.tickvals.join(',') : ''
  );

  return (
    <div className="space-y-3">
      {/* Custom Tick Values */}
      <ControlGroup label="Custom tick values">
        <Input
          placeholder="0,10,20,30"
          value={customTicks}
          onChange={(e) => {
            setCustomTicks(e.target.value);
            const values = e.target.value
              .split(',')
              .map((v) => parseFloat(v.trim()))
              .filter((v) => !isNaN(v));
            if (values.length > 0) {
              updateAxis('x', 'tickvals', values);
            } else {
              updateAxis('x', 'tickvals', undefined);
            }
          }}
        />
      </ControlGroup>

      {/* Tick Angle */}
      <ControlGroup label="Tick angle">
        <Select
          value={String(xaxis.tickangle || 0)}
          onValueChange={(value) => updateAxis('x', 'tickangle', parseInt(value))}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">0°</SelectItem>
            <SelectItem value="-45">-45°</SelectItem>
            <SelectItem value="-90">-90°</SelectItem>
            <SelectItem value="45">45°</SelectItem>
          </SelectContent>
        </Select>
      </ControlGroup>
    </div>
  );
}

function YAxisPrimaryGroup() {
  const { plotlyJson, dataColumns } = usePlotEditorContext();
  const { updateAxis, updateLayout } = usePlotlyUpdate();
  
  const yaxis = plotlyJson?.layout?.yaxis || {};
  const [yRange, setYRange] = useState({
    min: yaxis.range?.[0] ?? '',
    max: yaxis.range?.[1] ?? '',
  });

  useEffect(() => {
    const range = plotlyJson?.layout?.yaxis?.range;
    if (range) {
      setYRange({
        min: range[0] ?? '',
        max: range[1] ?? '',
      });
    }
  }, [plotlyJson?.layout?.yaxis?.range]);

  const formatOptions = [
    { value: 'default', label: 'Default' },
    { value: '.0f', label: 'Integer' },
    { value: '.1f', label: '1 Decimal' },
    { value: '.2f', label: '2 Decimals' },
    { value: '.0%', label: 'Percentage' },
    { value: '.2e', label: 'Scientific' },
    { value: '$,.0f', label: 'Currency' },
  ];

  return (
    <div className="space-y-3">
      {/* Data Column */}
      {dataColumns && dataColumns.length > 0 && (
        <ControlGroup label="Data column">
          <Select
            defaultValue={plotlyJson?.data?.[0]?.y}
            onValueChange={(value) => updateLayout('yaxis.column', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select column" />
            </SelectTrigger>
            <SelectContent>
              {dataColumns.map((col) => (
                <SelectItem key={col} value={col}>
                  {col}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ControlGroup>
      )}

      {/* Scale Type */}
      <ControlGroup label="Scale type">
        <Select
          value={yaxis.type || 'linear'}
          onValueChange={(value) => updateAxis('y', 'type', value)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="linear">Linear</SelectItem>
            <SelectItem value="log">Logarithmic</SelectItem>
            <SelectItem value="category">Categorical</SelectItem>
          </SelectContent>
        </Select>
      </ControlGroup>

      {/* Number Format */}
      <ControlGroup label="Number format">
        <Select
          value={yaxis.tickformat || 'default'}
          onValueChange={(value) => {
            if (value === 'default') {
              updateAxis('y', 'tickformat', undefined);
            } else {
              updateAxis('y', 'tickformat', value);
            }
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select format" />
          </SelectTrigger>
          <SelectContent>
            {formatOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </ControlGroup>

      {/* Custom Range */}
      <ControlGroup label="Custom range">
        <div className="grid grid-cols-2 gap-2">
          <Input
            type="number"
            placeholder="Min"
            value={yRange.min}
            onChange={(e) => {
              const newRange = { ...yRange, min: e.target.value };
              setYRange(newRange);
              const range = [
                newRange.min ? parseFloat(newRange.min) : null,
                newRange.max ? parseFloat(newRange.max) : null,
              ];
              updateAxis('y', 'range', range);
            }}
          />
          <Input
            type="number"
            placeholder="Max"
            value={yRange.max}
            onChange={(e) => {
              const newRange = { ...yRange, max: e.target.value };
              setYRange(newRange);
              const range = [
                newRange.min ? parseFloat(newRange.min) : null,
                newRange.max ? parseFloat(newRange.max) : null,
              ];
              updateAxis('y', 'range', range);
            }}
          />
        </div>
      </ControlGroup>

      {/* Label Position */}
      <ControlGroup label="Label position">
        <Select
          value={yaxis.side || 'left'}
          onValueChange={(value) => updateAxis('y', 'side', value)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="left">Left</SelectItem>
            <SelectItem value="right">Right</SelectItem>
          </SelectContent>
        </Select>
      </ControlGroup>

      {/* Gridlines */}
      <div className="pt-2 border-t border-border/50">
        <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground mb-2">
          Gridlines
        </p>
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-muted-foreground">Show gridlines</span>
          <Switch
            checked={yaxis.showgrid !== false}
            onCheckedChange={(checked) => updateAxis('y', 'showgrid', checked)}
          />
        </div>
      </div>
    </div>
  );
}

function YAxisAdvancedGroup() {
  const { plotlyJson } = usePlotEditorContext();
  const { updateAxis } = usePlotlyUpdate();
  const yaxis = plotlyJson?.layout?.yaxis || {};
  const [customTicks, setCustomTicks] = useState(
    Array.isArray(yaxis.tickvals) ? yaxis.tickvals.join(',') : ''
  );

  return (
    <div className="space-y-3">
      {/* Custom Tick Values */}
      <ControlGroup label="Custom tick values">
        <Input
          placeholder="0,10,20,30"
          value={customTicks}
          onChange={(e) => {
            setCustomTicks(e.target.value);
            const values = e.target.value
              .split(',')
              .map((v) => parseFloat(v.trim()))
              .filter((v) => !isNaN(v));
            if (values.length > 0) {
              updateAxis('y', 'tickvals', values);
            } else {
              updateAxis('y', 'tickvals', undefined);
            }
          }}
        />
      </ControlGroup>

      {/* Tick Angle */}
      <ControlGroup label="Tick angle">
        <Select
          value={String(yaxis.tickangle || 0)}
          onValueChange={(value) => updateAxis('y', 'tickangle', parseInt(value))}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">0°</SelectItem>
            <SelectItem value="-45">-45°</SelectItem>
            <SelectItem value="-90">-90°</SelectItem>
          </SelectContent>
        </Select>
      </ControlGroup>
    </div>
  );
}

